import React, { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next";
import DataTableClientInfo from "../../../../components/TableElement/DataTableClientInfo/DataTableClientInfo";
import { convertDateToLongString } from "../../../../tools/convertDateToString";
import { SUCCESS } from "../../../../store/globalStatuses/globalStatuses";
import Modal from "react-modal";
import styles from './ClientInfo.module.scss';
import closeModalImage from "../../../../components/PayModal/assets/CloseModalIcon.svg";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import phoneImg from "../../../Analytics/components/RFMClients/assets/images/phone.png";
import branchApi from "../../../../store/branchStore/branchApi";
import { store } from "react-notifications-component";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import clientInfoActions from "../../../../store/clientInfoStore/clientInfoActions";
import { formatDate } from "../../../../helpers";
import { Roles } from "../../../../config";
import { useCurrentBranch } from "../../../../hooks/useCurrentBranch";
import { BadgeTag, BadgeTagSmall } from "../../../../components/Badge";
import TagCustomerFormModal from "../../../../components/TagCustomerFormModal";
import BonusBreakdown from "../BonusBreakdown";
import { BONUS_SOURCES } from "../../../../config/bonusSources";
import clientInfoApi from "../../../../store/clientInfoStore/clientInfoApi";
import { isArgymaqBranch } from "../../../../config/hardcodedBusinesses";

const RESTRICTED_TAG_BUSINESS_IDS = ["1990319019407708160", "2024707981702074368", "2024709367416229888", "1990320835134492672", "2034216763209551872", "2034213686763065344", "2037054083965128704"];

function hasBreakdownShape(userBalance) {
    if (!userBalance || typeof userBalance !== 'object' || Array.isArray(userBalance)) {
        return false;
    }

    return BONUS_SOURCES.some((source) => {
        const sourceData = userBalance[source.key];
        return Array.isArray(sourceData) || Array.isArray(sourceData?.bonuses);
    });
}

function extractBreakdown(payload) {
    const candidates = [
        payload?.user_balance,
        payload?.data?.user_balance,
        payload?.data,
        payload,
    ];

    return candidates.find(hasBreakdownShape) || null;
}

const modalStyles = {
    overlay: {
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: 'none',
        padding: 0,
        borderRadius: 24,
    }
};

export default function ClientInfo(props) {
    const { t } = useTranslation();
    const {
        customer,
        cStatus,
        page,
        transactions,
        tStatus,
        changeTransactionPageAction,
        count,
        getBackToClients,
        setBonusesModal,
        bonusesModal,
        info,
        status,
    } = props;
    const { id } = useParams();
    const dispatch = useDispatch();
    const [amountSum, setAmountSum] = useState('');
    const [isSent, setIsSent] = useState(false);
    const [pending, setPending] = useState(false);
    const [sendModal, setSendModal] = useState(false);
    const [pushMessage, setPushMessage] = useState('');
    const [bonusModal, setBonusModal] = useState(false);
    const [bonusModalStep, setBonusModalStep] = useState(1);
    const [bonus, setBonus] = useState(0);
    const [modalPending, setModalPending] = useState(false);
    const [tagsModal, setTagsModal] = useState(false);
    const initialUserBalance = hasBreakdownShape(customer?.user_balance) ? customer.user_balance : null;
    const [balanceBreakdown, setBalanceBreakdown] = useState(initialUserBalance);
    const { customer: customerInfo } = useSelector(s => s.clientInfoReducer);
    const { currentRole } = useCurrentBranch();
    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [tagModalUserId, setTagModalUserId] = useState('');
    const params = Object.fromEntries(new URLSearchParams(location.search));

    const isTagEditRestricted = RESTRICTED_TAG_BUSINESS_IDS.includes(String(info?.id)) && currentRole !== Roles.ADMINISTRATOR;
    const isBonusEditRestricted = isArgymaqBranch(info?.id) && currentRole !== Roles.ADMINISTRATOR;

    const isCascade = !!info?.configuration?.private_cashback?.is_cascade;
    const customerBreakdown = hasBreakdownShape(customer?.user_balance) ? customer.user_balance : null;
    const resolvedUserBalance = customerBreakdown || balanceBreakdown;
    const totalBonuses = Math.floor(customer.bonuses || 0);
    const tags = customer?.tags || [];
    const previewTags = tags.slice(0, 2);

    useEffect(() => {
        if (customerBreakdown) {
            setBalanceBreakdown(customerBreakdown);
            return;
        }

        if (!isCascade || !customer?.id || !info?.id || !info?.group_id || totalBonuses <= 0) {
            setBalanceBreakdown(null);
            return;
        }

        let cancelled = false;
        setBalanceBreakdown(null);

        clientInfoApi.getClientInfo(customer.id, info)
            .then((res) => {
                if (cancelled) return;
                setBalanceBreakdown(extractBreakdown(res));
            })
            .catch(() => {
                if (cancelled) return;
                setBalanceBreakdown(null);
            });

        return () => {
            cancelled = true;
        };
    }, [customer?.id, customerBreakdown, info?.group_id, info?.id, isCascade, totalBonuses]);

    const changeAmountSum = (e) => {
        if (isNaN(Number(e.target.value))) return;
        setAmountSum(e.target.value);
    };

    const onChangePrice = (e) => {
        if (isFinite(+e.currentTarget.value)) {
            if (!info) return;
            setBonus(+e.currentTarget.value);
        }
    };

    const sendBonus = () => {
        if (!info || !customer.id) return;
        setPending(true);
        branchApi.sendBonusesToUsers(info.id, { user_ids: [customer.id], group_id: info.group_id, amount: Number(amountSum) })
            .then(() => {
                setIsSent(true);
            })
            .finally(() => {
                setPending(false);
            });
    };

    const onCloseModal = () => {
        if (isSent) setIsSent(false);
        setBonusesModal(false);
        setAmountSum('');
    };

    const openBonusesModal = () => {
        setBonusModal(true);
    };

    const onChangePushMessage = (e) => {
        if (e.target.value.length < 150) {
            setPushMessage(e.target.value);
        }
    };

    const onSendPushToCustomers = () => {
        if (!customerInfo || !customerInfo.id || !pushMessage) return;

        setModalPending(true);
        branchApi.sendPushToCustomers(info.id, { user_ids: [customerInfo.id], message: pushMessage })
            .then(() => {
                store.addNotification({
                    title: t('success'),
                    message: t('successMessageSent'),
                    type: "success",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                });
                setPushMessage('');
            })
            .finally(() => {
                setModalPending(false);
                setSendModal(false);
            });
    };

    const onSendBonusesToUsers = () => {
        if (!info || !bonus || !customer.id) return;

        setModalPending(true);
        branchApi.sendBonusesToUsers(info.id, { user_ids: [customer.id], group_id: info.group_id, amount: bonus })
            .then(() => {
                store.addNotification({
                    title: t('success'),
                    message: t('NSClients.successBonusEdit'),
                    type: "success",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                });
            })
            .finally(() => {
                setModalPending(false);
                setBonus(0);
                setBonusModalStep(1);
                setBonusModal(false);
            });
    };

    const renderBonusExpiration = () => {
        if (customer.bonuses_expire !== 0 && !customer.bonuses_expire) {
            return null;
        }

        if (isCascade) {
            return t('NSBonusMode.cascade');
        }

        return customer.bonuses_expire === 0 ? '∞' : formatDate(customer.bonuses_expire);
    };

    const getClientName = () => {
        if (cStatus !== SUCCESS) return '-';
        if (customer.first_name.length && customer.last_name.length) {
            return `${customer.first_name} ${customer.last_name}`;
        }
        if (customer.first_name.length) return customer.first_name;
        if (customer.last_name.length) return customer.last_name;
        return '-';
    };

    const getClientStatus = () => {
        if (cStatus !== SUCCESS) return '-';
        if (customer.client_type === "RETURNED") {
            return <span className={styles.loyalStatus}>{t('loyal1')}</span>;
        }
        if (customer.client_type === "NEW") {
            return <span className={styles.newStatus}>{t('new1')}</span>;
        }
        return '-';
    };

    const renderValueBox = (content, className = '') => (
        <div className={`${styles.valueBox} ${className}`.trim()}>
            {content}
        </div>
    );

    const renderBonusesCard = () => {
        if (!isCascade) {
            return renderValueBox(cStatus === SUCCESS ? totalBonuses : '0');
        }
        return renderValueBox(
            <>
                <div className={styles.bonusValueHeader}>
                    <span className={styles.bonusValueAmount}>{cStatus === SUCCESS ? totalBonuses : '0'}</span>
                    <span className={styles.cascadePill}>{t('NSBonusMode.cascade')}</span>
                </div>
                {resolvedUserBalance && (
                    <BonusBreakdown userBalance={resolvedUserBalance} />
                )}
            </>,
            styles.bonusValueCard
        );
    };

    const renderTagsCard = () => {
        if (!tags.length) {
            return renderValueBox('-', styles.tagsValueCard);
        }

        return renderValueBox(
            <>
                <div className={styles.tagsPreview}>
                    {previewTags.map((tag) => (
                        <BadgeTagSmall
                            key={`${tag.name}-${tag.background_color}-${tag.color}`}
                            style={{ backgroundColor: tag.background_color, color: tag.color }}
                        >
                            {tag.name}
                        </BadgeTagSmall>
                    ))}
                </div>
                <button
                    type="button"
                    className={styles.inlineAction}
                    onClick={() => setTagsModal(true)}
                >
                    {t('show')}
                    {tags.length > 2 ? ` (${tags.length})` : ''}
                </button>
            </>,
            styles.tagsValueCard
        );
    };

    const summaryRows = [
        {
            key: 'name',
            leftLabel: t('NSRespondents.name'),
            leftValue: renderValueBox(getClientName()),
            rightLabel: t('sumPayments'),
            rightValue: renderValueBox(cStatus === SUCCESS && customer.total_amount > 0 ? `${customer.total_amount}` : '0'),
        },
        {
            key: 'phone',
            leftLabel: t('NSAuth.phone'),
            leftValue: renderValueBox(cStatus === SUCCESS && customer.phone.length ? customer.phone : '-'),
            rightLabel: t('NSGeneral.average'),
            rightValue: renderValueBox(cStatus === SUCCESS && customer.average_check > 0 ? `${Math.round(customer.average_check)}` : '0'),
        },
        {
            key: 'status',
            leftLabel: t('status'),
            leftValue: renderValueBox(getClientStatus(), styles.statusValueBox),
            rightLabel: t('dateOfFirstBuy'),
            rightValue: renderValueBox(cStatus === SUCCESS && customer.first_order ? convertDateToLongString(customer.first_order) : '-'),
        },
        {
            key: 'bonuses',
            leftLabel: t('bonuses'),
            leftValue: renderBonusesCard(),
            rightLabel: t('dateOfLastBuy'),
            rightValue: renderValueBox(cStatus === SUCCESS && customer.last_order > 0 ? convertDateToLongString(customer.last_order) : '-'),
        },
        {
            key: 'awards',
            leftLabel: t('NSSidebar.awards'),
            leftValue: renderValueBox(`${customer.received_stamp_count || '0'}/${customer.stamp_count || '0'}`),
            rightLabel: t('NSClients.visitsCount'),
            rightValue: renderValueBox(customer.transactions_count || '0'),
        },
        {
            key: 'comeFrom',
            leftLabel: t('NSClients.сameFrom'),
            leftValue: renderValueBox(customer.come_from || t('NSComparison.notPoint')),
            rightLabel: t('certificateBalance'),
            rightValue: renderValueBox(customer.certificate_balance || '0'),
        },
        {
            key: 'expiration',
            leftLabel: t('NSBonus.expirationBonus'),
            leftValue: renderValueBox(renderBonusExpiration() || '-'),
            rightLabel: t('takenGifts'),
            rightValue: renderValueBox(customer.total_awards || '0'),
        },
        {
            key: 'tags',
            leftLabel: t('NSTag.tags'),
            leftValue: renderTagsCard(),
            rightLabel: t('NSPayModal.currentStampsCount'),
            rightValue: renderValueBox(customer.received_stamp_count || '0'),
        },
    ];

    if (customer.birthday) {
        summaryRows.push({
            key: 'birthday',
            leftLabel: '',
            leftValue: null,
            rightLabel: t('NSBonus.birthday'),
            rightValue: renderValueBox(customer.birthday || '-'),
        });
    }

    let editParams = '';

    if (currentRole === Roles.ADMINISTRATOR) {
        editParams = '?from=database';
    }

    if (params.groupId) {
        if (editParams.length) {
            editParams += `&groupId=${params.groupId}`;
        } else {
            editParams = `?groupId=${params.groupId}`;
        }
    }

    return (
        <div className={styles.container}>
            <Modal open={bonusesModal} onClickClose={onCloseModal}>
                {isSent ? (
                    <>
                        <div className={styles.header}>
                            <p>{t('NSBonusModal.success')}</p>
                        </div>
                        <div className={styles.sendButton}>
                            <button onClick={onCloseModal} disabled={pending}>
                                {t('close')}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.header}>
                            <p>{t('NSPayment.assessment')}</p>
                            {(!!customer.first_name || !!customer.last_name) && (
                                <span>{`${customer.first_name} ${customer.last_name}`}</span>
                            )}
                        </div>
                        <div className={styles.points}>
                            <p>{t('scoreCount')}:</p>
                            <span>{customer.bonuses > 0 ? Math.round(customer.bonuses) : 0}</span>
                        </div>

                        <div className={styles.getPoints}>
                            <p>{t('NSClients.bonusCount')}</p>
                            <input value={amountSum} onChange={changeAmountSum} />
                        </div>

                        <div className={styles.sendButton}>
                            <button onClick={sendBonus} disabled={pending}>
                                {t('NSClients.editBonus')}
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            <i
                className="fa fa-chevron-left"
                aria-hidden="true"
                style={{ position: "absolute", left: 34, top: 54, cursor: 'pointer' }}
                onClick={getBackToClients}
            />

            <div className={styles.summaryGrid}>
                {summaryRows.map((row) => (
                    <div className={styles.summaryRow} key={row.key}>
                        <div className={`${styles.summaryLabel} ${!row.leftLabel ? styles.emptyCell : ''}`}>
                            {row.leftLabel ? `${row.leftLabel}:` : ''}
                        </div>
                        <div className={`${styles.summaryValue} ${!row.leftValue ? styles.emptyCell : ''}`}>
                            {row.leftValue}
                        </div>
                        <div className={styles.summaryLabel}>
                            {row.rightLabel ? `${row.rightLabel}:` : ''}
                        </div>
                        <div className={styles.summaryValue}>
                            {row.rightValue}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.actionsRow}>
                {!isBonusEditRestricted && (
                <button
                    onClick={openBonusesModal}
                    disabled={!customer.id}
                    className={styles.button}
                    style={{ opacity: `${!customer.id ? .3 : 1}` }}
                >
                    {t('NSClients.editBonus')}
                </button>
                )}
                <ButtonFilled variant="purple" onClick={() => setSendModal(true)}>
                    {t('NSStatistics.notification')}
                </ButtonFilled>
                <ButtonFilled variant="green" to={`/clients/client/edit/${id}${editParams}`}>
                    {t('edit')}
                </ButtonFilled>
                {!isTagEditRestricted && (
                <button
                    onClick={() => {
                        setTagModalUserId(customer.id);
                        setIsTagModalOpen(true);
                    }}
                    disabled={!customer.id}
                    className={styles.button}
                    style={{ opacity: `${!customer.id ? .3 : 1}` }}
                >
                    {t('NSTag.tags')}
                </button>
                )}
            </div>

            <h3>
                {t('NSGeneral.trans')}
                <span style={{ marginLeft: 12, fontSize: 12, fontWeight: 400, color: '#888' }}>
                    {t('NSClientInfo.groupScopeNote')}
                </span>
            </h3>
            <DataTableClientInfo
                transactions={transactions}
                tStatus={tStatus}
                page={page}
                changeTransactionPageAction={changeTransactionPageAction}
                count={count}
            />

            <Modal
                ariaHideApp={false}
                isOpen={tagsModal}
                style={modalStyles}
                onRequestClose={() => setTagsModal(false)}
            >
                <div className={styles.modal}>
                    <div className={styles.closeModal} onClick={() => setTagsModal(false)}>
                        <img src={closeModalImage} alt="" />
                    </div>
                    <div className={styles.modalContainer}>
                        <div className={styles.header}>
                            <p>{t('NSTag.tags')}</p>
                        </div>
                        <div className={styles.tagsModalList}>
                            {tags.length ? tags.map((tag) => (
                                <BadgeTag
                                    key={`${tag.name}-${tag.background_color}-${tag.color}`}
                                    style={{ backgroundColor: tag.background_color, color: tag.color }}
                                >
                                    {tag.name}
                                </BadgeTag>
                            )) : (
                                <div className={styles.tagsModalEmpty}>-</div>
                            )}
                        </div>
                        <ButtonFilled className={styles.btn} onClick={() => setTagsModal(false)}>
                            {t('close')}
                        </ButtonFilled>
                    </div>
                </div>
            </Modal>

            <Modal
                ariaHideApp={false}
                isOpen={bonusModal}
                style={modalStyles}
                onRequestClose={() => setBonusModal(false)}
            >
                <div className={styles.modal}>
                    <div className={styles.closeModal} onClick={() => setBonusModal(false)}>
                        <img src={closeModalImage} alt="" />
                    </div>
                    <div className={styles.modalContainer}>
                        {bonusModalStep == 1 ? (
                            <>
                                <div className={styles.header}>
                                    <p>{t('NSPayment.assessment')}</p>
                                </div>
                                <div className={styles.bonuses}>
                                    <p className={styles.subTitle} style={{ marginTop: 80 }}>
                                        {t('NSClients.bonusCount')}
                                    </p>
                                    <input
                                        style={{ marginBottom: 24 }}
                                        value={bonus}
                                        className={styles.inputBonus}
                                        onChange={onChangePrice}
                                    />

                                    <ButtonFilled
                                        className={styles.btn}
                                        disabled={!bonus}
                                        onClick={() => {
                                            setBonusModalStep(2);
                                        }}
                                    >
                                        {t('proceed')}
                                    </ButtonFilled>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.header}>
                                    <div
                                        style={{ cursor: 'pointer', marginRight: "auto", marginBottom: 40 }}
                                        onClick={() => setBonusModalStep(1)}
                                    >
                                        <svg style={{ marginRight: 'auto' }} width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect width="2.70393" height="15.6376" rx="1.35196" transform="matrix(-0.707107 -0.707107 -0.707107 0.707107 13.0293 2.64099)" fill="#AFAFAB" />
                                            <rect x="12.9746" y="21.1027" width="2.62964" height="15.6376" rx="1.31482" transform="rotate(135 12.9746 21.1027)" fill="#AFAFAB" />
                                        </svg>
                                    </div>
                                </div>
                                <div className={styles.bonusesResult}>
                                    <div><span>{t('NSClients.giveBonus')}</span> <span>{bonus}</span></div>
                                    <div className={styles.bonusesResultItem}><b>{t('NSClients.toSum')}</b> <span>{bonus * 1} ₸</span></div>

                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', margin: '50px auto 0', height: '70%' }}>
                                        <ButtonFilled
                                            className={styles.btn}
                                            loading={modalPending}
                                            onClick={onSendBonusesToUsers}
                                        >
                                            {t('NSClients.submitPayment')}
                                        </ButtonFilled>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal
                ariaHideApp={false}
                isOpen={sendModal}
                style={modalStyles}
                onRequestClose={() => setSendModal(false)}
            >
                <div className={styles.modal}>
                    <div className={styles.closeModal} onClick={() => setSendModal(false)}>
                        <img src={closeModalImage} alt="" />
                    </div>
                    <div className={styles.modalContainer}>
                        <div className={styles.header}>
                            <p>{t('NSClients.not')}</p>
                        </div>

                        <div>
                            <p className={styles.p}>{t('NSClients.textNot')}</p>
                            <textarea
                                placeholder={t('Form.enterText')}
                                className={styles.textArea}
                                value={pushMessage}
                                onChange={onChangePushMessage}
                                required
                            />
                            <div className={styles.span} style={{ color: `${pushMessage.length >= 149 ? 'tomato' : '#000000'}` }}>
                                {t('maxSym', { sym: 150 })}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px' }}>
                                <img width="230" src={phoneImg} style={{ marginBottom: '16px' }} alt="" />
                                <ButtonFilled className={styles.btn} onClick={onSendPushToCustomers} loading={modalPending}>
                                    {t('send')}
                                </ButtonFilled>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            <TagCustomerFormModal
              isOpen={isTagModalOpen}
              setIsOpen={setIsTagModalOpen}
              userId={tagModalUserId}
              setUserId={setTagModalUserId}
              users={customer ? [{
                  id: customer.id,
                  first_name: customer.first_name || '',
                  last_name: customer.last_name || '',
                  phone: customer.phone || '',
                  user_tags: customer.tags || [],
              }] : []}
              handleSuccessFormSubmit={() => {
                  setIsTagModalOpen(false);
                  setTagModalUserId('');
                  dispatch(clientInfoActions.getClientInfo(id, info, currentRole, params));
              }}
            />
        </div>
    );
}
